import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData } from '@/mocks/comics';

export default function About() {
  const [contactStatus, setContactStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const hpVal = (formData.get('company_alt') as string || '').trim();
    if (hpVal) {
      setContactStatus('success');
      form.reset();
      return;
    }
    setContactStatus('loading');
    try {
      const payload = new URLSearchParams();
      payload.append('name', formData.get('name') as string);
      payload.append('email', formData.get('email') as string);
      payload.append('message', formData.get('message') as string);
      const res = await fetch('http://vktest.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        if (serverMsg.includes('spam')) {
          setErrorMsg('提交失败，请稍后再试。');
        } else {
          setErrorMsg(serverMsg || '提交失败，请稍后再试。');
        }
        setContactStatus('error');
      }
    } catch {
      setContactStatus('error');
      setErrorMsg('网络错误，请检查连接后重试。');
    }
  };

  const statBgColors = ['bg-accent-100/60', 'bg-secondary-100/60', 'bg-background-200/70', 'bg-primary-100/60'];
  const statIconColors = ['text-accent-700', 'text-secondary-700', 'text-foreground-700', 'text-primary-700'];
  const statIcons = ['ri-book-open-line', 'ri-user-heart-line', 'ri-eye-line', 'ri-upload-cloud-line'];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "韩漫漫画网是否需要付费？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "韩漫漫画网目前所有漫画内容均为免费阅读，无需付费订阅。我们通过广告收入维持平台运营，为读者提供高品质的免费漫画服务。"
        }
      },
      {
        "@type": "Question",
        "name": "如何找到我想看的漫画？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "您可以通过首页的分类导航浏览不同题材的漫画，也可以使用搜索功能直接查找漫画名称或作者。我们提供恋爱、悬疑、都市、奇幻等多种分类供您选择。"
        }
      },
      {
        "@type": "Question",
        "name": "漫画多久更新一次？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "大部分热门连载漫画每周更新1-2话，具体更新频率请关注各漫画作品页面的更新提示。我们会在第一时间同步最新的韩漫画源。"
        }
      },
      {
        "@type": "Question",
        "name": "可以在手机上阅读吗？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "当然可以！韩漫漫画网采用响应式设计，完美适配手机、平板和电脑等各种设备。无论您使用什么设备，都能享受流畅的下拉式阅读体验。"
        }
      },
    ]
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "韩漫漫画网",
    "url": "http://vktest.com/",
    "description": "韩漫漫画网是专业的韩国漫画在线阅读平台，提供免费高清韩漫阅读服务。",
    "foundingDate": "2020",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": aboutData.contact.email,
      "telephone": aboutData.contact.phone,
      "availableLanguage": ["Chinese"]
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": aboutData.contact.address
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <section className="bg-background-50 py-10 md:py-16">
          <div className="px-4 md:px-6">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '关于我们' },
              ]}
            />

            <div className="max-w-3xl">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">关于韩漫网</span>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
                为每一位韩漫爱好者
                <br />
                打造最好的阅读平台
              </h1>
              <p className="text-foreground-500 text-base leading-relaxed max-w-2xl">
                {aboutData.brandName}成立于{aboutData.foundedYear}年，致力于为全球韩漫爱好者提供免费、高清、流畅的漫画阅读体验。我们坚信好的漫画应当被更多人看到，好的阅读体验不应有门槛。
              </p>
            </div>
          </div>
        </section>

        <section className="bg-foreground-900 py-12 md:py-16">
          <div className="px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {aboutData.stats.map((stat, index) => (
                <div key={stat.label} className={`rounded-xl ${statBgColors[index] || 'bg-background-200/70'} p-6 text-center`}>
                  <div className={`w-10 h-10 mx-auto mb-3 rounded-full flex items-center justify-center bg-background-50`}>
                    <i className={`${statIcons[index]} ${statIconColors[index]} text-lg`}></i>
                  </div>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">{stat.value}</div>
                  <div className="text-foreground-500 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-12 md:py-16">
          <div className="px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">我们的口碑</h2>
                <p className="text-foreground-500 text-sm leading-relaxed mb-6">
                  自上线以来，韩漫漫画网获得了广大读者的认可与好评。208万活跃用户的选择，是对我们最大的肯定。我们始终坚持内容质量与用户体验并重，为读者提供最优质的韩漫阅读服务。
                </p>

                <div className="space-y-4">
                  {[
                    { label: '内容品质', desc: '严格筛选每一部作品，确保画质与内容的高水准', icon: 'ri-award-line' },
                    { label: '更新速度', desc: '第一时间同步最新韩漫画源，追更不再等待', icon: 'ri-speed-up-line' },
                    { label: '阅读体验', desc: '无遮挡下拉式阅读，简洁界面零干扰', icon: 'ri-device-line' },
                  ].map((item) => (
                    <div key={item.label} className="flex gap-4 p-4 rounded-xl bg-background-100">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-primary-600`}></i>
                      </div>
                      <div>
                        <h4 className="font-heading text-sm font-semibold text-foreground-900">{item.label}</h4>
                        <p className="text-foreground-500 text-xs mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20creative%20team%20working%20together%20in%20modern%20office%2C%20warm%20collaborative%20atmosphere%2C%20soft%20natural%20light%2C%20diverse%20young%20professionals%2C%20editorial%20style%20photography%2C%20clean%20composition&width=1000&height=750&seq=about-team&orientation=landscape"
                  alt="韩漫漫画网团队"
                  title="韩漫漫画网编辑团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              常见问题
            </h2>

            <div className="max-w-3xl mx-auto space-y-3">
              {[
                { q: '韩漫漫画网是否需要付费？', a: '韩漫漫画网目前所有漫画内容均为免费阅读，无需付费订阅。我们通过广告收入维持平台运营，为读者提供高品质的免费漫画服务。' },
                { q: '如何找到我想看的漫画？', a: '您可以通过首页的分类导航浏览不同题材的漫画，也可以使用搜索功能直接查找漫画名称或作者。我们提供恋爱、悬疑、都市、奇幻等多种分类供您选择。' },
                { q: '漫画多久更新一次？', a: '大部分热门连载漫画每周更新1-2话，具体更新频率请关注各漫画作品页面的更新提示。我们会在第一时间同步最新的韩漫画源。' },
                { q: '可以在手机上阅读吗？', a: '当然可以！韩漫漫画网采用响应式设计，完美适配手机、平板和电脑等各种设备。无论您使用什么设备，都能享受流畅的下拉式阅读体验。' },
              ].map((faq) => (
                <details key={faq.q} className="group rounded-xl bg-background-50 p-5 cursor-pointer">
                  <summary className="flex items-center justify-between font-heading text-sm font-semibold text-foreground-900 list-none">
                    {faq.q}
                    <i className="ri-add-line group-open:hidden text-foreground-400"></i>
                    <i className="ri-subtract-line hidden group-open:block text-foreground-400"></i>
                  </summary>
                  <p className="mt-3 text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-12 md:py-16">
          <div className="px-4 md:px-6 max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-2">联系我们</h2>
            <p className="text-foreground-500 text-sm text-center mb-8">有任何问题或建议？欢迎随时联系我们。</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              <div className="rounded-xl bg-background-100 p-5 text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">邮箱</h4>
                <p className="text-foreground-500 text-xs">{aboutData.contact.email}</p>
              </div>
              <div className="rounded-xl bg-background-100 p-5 text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-phone-line text-accent-600"></i>
                </div>
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电话</h4>
                <p className="text-foreground-500 text-xs">{aboutData.contact.phone}</p>
              </div>
              <div className="rounded-xl bg-background-100 p-5 text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-map-pin-line text-secondary-600"></i>
                </div>
                <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">地址</h4>
                <p className="text-foreground-500 text-xs">{aboutData.contact.address}</p>
              </div>
            </div>

            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300 text-foreground-900 text-sm outline-none focus:border-primary-400 transition-colors"
                    placeholder="您的姓名"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300 text-foreground-900 text-sm outline-none focus:border-primary-400 transition-colors"
                    placeholder="hello@vktest.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300 text-foreground-900 text-sm outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（500字以内）"
                ></textarea>
                <p className="text-foreground-400 text-xs mt-1">最多500个字符</p>
              </div>
              <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
              <button
                type="submit"
                disabled={contactStatus === 'loading'}
                className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
              >
                {contactStatus === 'loading' ? '发送中...' : '发送留言'}
              </button>
              {contactStatus === 'success' && (
                <p className="text-center text-secondary-600 text-sm">留言已发送，我们会尽快回复您！</p>
              )}
              {contactStatus === 'error' && (
                <p className="text-center text-accent-600 text-sm">{errorMsg}</p>
              )}
            </form>
          </div>
        </section>
      </main>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <Footer />
    </>
  );
}